import {
  LayoutPanelState,
  LayoutDefinition as LayoutState,
} from "./LayoutRecord";
import { getLayoutSchema, LATEST_LAYOUT_SCHEMA_VERSION } from "./schemas";

export class InvalidLayoutDefinitionError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "InvalidLayoutDefinitionError";
  }
}

export class LayoutDefinition {
  readonly #state: LayoutState;
  readonly #schemaVersion: number;

  /**
   * Checks a layout definition that came from outside this code (a saved
   * record, or one carried in navigation state) against the schema for the
   * version it was written under, and keeps that version. Reading does not
   * migrate; a definition moves to a newer schema only when
   * LayoutDefinitionBuilder.build rewrites it.
   *
   * @throws {InvalidLayoutDefinitionError} if the object does not match that schema
   * @throws {UnsupportedLayoutSchemaVersion} if no schema is registered for the given version
   */
  static fromObj(obj: unknown, schemaVersion: number): LayoutDefinition {
    if (LayoutDefinition.isValid(obj, schemaVersion)) {
      return new LayoutDefinition(obj, schemaVersion);
    }
    throw new InvalidLayoutDefinitionError(
      "Input is not a valid layout definition",
    );
  }

  /**
   * @throws {UnsupportedLayoutSchemaVersion} if no schema is registered for the given version
   */
  private static isValid(
    obj: unknown,
    schemaVersion: number,
  ): obj is LayoutState {
    const schema = getLayoutSchema(schemaVersion);
    return schema.safeParse(obj).success;
  }

  /**
   * Takes the state as handed over: no schema check, and the caller's word for
   * which version it was written under. Code that later reads panels or rules
   * off the definition therefore cannot assume the shapes the schema would
   * have enforced. A definition that came from a saved record or from
   * navigation state should go through `fromObj`, which validates.
   */
  constructor(layoutDefinition: LayoutState, schemaVersion: number);
  /**
   * Starts an empty layout (no panels, no rules, an empty root container) at
   * the latest schema version.
   */
  constructor();
  constructor(layoutDefinition?: LayoutState, schemaVersion?: number) {
    if (!layoutDefinition) {
      this.#state = {
        panels: {},
        layout: {
          id: "root",
          axis: "x",
          relativeSize: 1,
          children: [],
        },
        rules: [],
      };
      this.#schemaVersion = LATEST_LAYOUT_SCHEMA_VERSION;
      return;
    }

    if (schemaVersion === undefined) {
      throw new Error(
        "Schema version must be provided when layout definition is provided",
      );
    }

    this.#state = layoutDefinition;
    this.#schemaVersion = schemaVersion;
  }

  get panels(): LayoutPanelState[] {
    return Object.values(this.#state.panels);
  }

  get rules(): LayoutState["rules"] {
    return this.#state.rules;
  }

  get state(): LayoutState {
    return this.#state;
  }

  get schemaVersion(): number {
    return this.#schemaVersion;
  }
}
